import React, { useLayoutEffect, useRef, useState } from "react";
import type { TEmailBuilderPreviewViewport } from "redux/reducers/emailBuilder/emailBuilder";
import {
  EMAIL_BUILDER_PREVIEW_IFRAME_SANDBOX,
  resolveEmailBuilderPreviewFrameMaxWidth,
} from "./emailBuilderPreviewConstants";
import { EmailBuilderPreviewLoadingFrame } from "./EmailBuilderPreviewLoadingFrame";
import { prepareEmailBuilderPreviewHtml } from "./prepareEmailBuilderPreviewHtml";

const PREVIEW_IFRAME_READY_SELECTOR = ".email-inner";

function measureEmailBuilderPreviewIframeHeight(iframe: HTMLIFrameElement): number | null {
  const doc = iframe.contentDocument;
  if (doc === null) {
    return null;
  }

  const height = Math.max(doc.documentElement?.scrollHeight ?? 0, doc.body?.scrollHeight ?? 0);
  return height > 0 ? height : null;
}

interface IEmailBuilderPreviewFrameProps {
  previewHtml: string;
  previewViewport: TEmailBuilderPreviewViewport;
  contentWidthPx: number;
  iframeTitle: string;
  onEscape?: () => void;
}

export function EmailBuilderPreviewFrame(props: IEmailBuilderPreviewFrameProps) {
  const { previewHtml, previewViewport, contentWidthPx, iframeTitle, onEscape } = props;
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const resizeFrameRef = useRef<number | null>(null);
  const previewSyncedHeightPxRef = useRef<number | null>(null);
  const detachPreviewIframeEscapeRef = useRef<(() => void) | null>(null);
  const detachPreviewIframeHeightObserversRef = useRef<(() => void) | null>(null);
  const lastPreviewSyncedHeightPxByViewportRef = useRef<
    Record<TEmailBuilderPreviewViewport, number | null>
  >({
    desktop: null,
    mobile: null,
  });
  const [heightReadyPreviewHtml, setHeightReadyPreviewHtml] = useState<string | null>(null);

  const cachedPreviewIframeMinHeightPx =
    lastPreviewSyncedHeightPxByViewportRef.current[previewViewport] ?? 0;
  const viewportMaxWidthPx = resolveEmailBuilderPreviewFrameMaxWidth(previewViewport);
  const showLoadingOverlay = heightReadyPreviewHtml !== previewHtml;
  const iframeMinHeightPx =
    !showLoadingOverlay && cachedPreviewIframeMinHeightPx > 0
      ? cachedPreviewIframeMinHeightPx
      : undefined;

  function clearPreviewIframeHeightSync() {
    if (resizeFrameRef.current !== null) {
      window.cancelAnimationFrame(resizeFrameRef.current);
    }
    resizeFrameRef.current = null;
  }

  function detachPreviewIframeHeightObservers() {
    detachPreviewIframeHeightObserversRef.current?.();
    detachPreviewIframeHeightObserversRef.current = null;
  }

  function schedulePreviewIframeHeightSync(iframe: HTMLIFrameElement) {
    if (resizeFrameRef.current !== null) {
      return;
    }

    resizeFrameRef.current = window.requestAnimationFrame(() => {
      resizeFrameRef.current = null;

      const height = measureEmailBuilderPreviewIframeHeight(iframe);
      const prev = previewSyncedHeightPxRef.current;
      if (height === null) {
        return;
      }

      if (height !== prev) {
        previewSyncedHeightPxRef.current = height;
        lastPreviewSyncedHeightPxByViewportRef.current[previewViewport] = height;
        iframe.style.height = `${height}px`;
      }

      setHeightReadyPreviewHtml(previewHtml);
    });
  }

  function isPreviewIframeDocumentReady(doc: Document): boolean {
    return doc.querySelector(PREVIEW_IFRAME_READY_SELECTOR) !== null;
  }

  function attachPreviewIframeEscape(iframe: HTMLIFrameElement) {
    detachPreviewIframeEscapeRef.current?.();

    const doc = iframe.contentDocument;
    if (doc === null) {
      detachPreviewIframeEscapeRef.current = null;
      return;
    }

    function handleKeydown(event: KeyboardEvent) {
      if (event.key !== "Escape" || event.defaultPrevented) {
        return;
      }

      event.preventDefault();
      onEscape?.();
    }

    doc.addEventListener("keydown", handleKeydown, true);
    detachPreviewIframeEscapeRef.current = () => {
      try {
        doc.removeEventListener("keydown", handleKeydown, true);
      } catch {
        // iframe document may be detached when preview unmounts
      }
    };
  }

  function attachPreviewIframeHeightSync(iframe: HTMLIFrameElement) {
    const doc = iframe.contentDocument;
    const body = doc?.body;
    if (doc?.documentElement == null || body == null) {
      return;
    }

    if (!isPreviewIframeDocumentReady(doc)) {
      return;
    }

    detachPreviewIframeHeightObservers();
    attachPreviewIframeEscape(iframe);

    const schedule = () => schedulePreviewIframeHeightSync(iframe);

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(schedule);
    });
    resizeObserver.observe(body);

    const imageCleanups: Array<() => void> = [];

    doc.querySelectorAll("img").forEach(image => {
      const onImageLayoutChange = () => schedule();
      image.addEventListener("load", onImageLayoutChange, { once: true });
      image.addEventListener("error", onImageLayoutChange, { once: true });
      imageCleanups.push(() => {
        image.removeEventListener("load", onImageLayoutChange);
        image.removeEventListener("error", onImageLayoutChange);
      });

      if (
        image.complete &&
        image.naturalWidth > 0 &&
        image.naturalHeight > 0 &&
        typeof image.decode === "function"
      ) {
        void image.decode().then(schedule, schedule);
      }
    });

    detachPreviewIframeHeightObserversRef.current = () => {
      resizeObserver.disconnect();
      imageCleanups.forEach(cleanup => cleanup());
    };

    schedule();
  }

  useLayoutEffect(() => {
    previewSyncedHeightPxRef.current = null;
    detachPreviewIframeEscapeRef.current?.();
    detachPreviewIframeEscapeRef.current = null;
    detachPreviewIframeHeightObservers();
    if (iframeRef.current !== null) {
      iframeRef.current.style.height = "";
    }
    clearPreviewIframeHeightSync();
  }, [previewHtml]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    previewSyncedHeightPxRef.current = null;
    detachPreviewIframeHeightObservers();
    if (iframeRef.current !== null) {
      iframeRef.current.style.height = "";
    }
    clearPreviewIframeHeightSync();
  }, [previewViewport]);

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (iframe === null || iframe.contentDocument?.documentElement == null) {
      return;
    }

    attachPreviewIframeHeightSync(iframe);
  }, [previewViewport, previewHtml]);

  useLayoutEffect(() => {
    return () => {
      detachPreviewIframeEscapeRef.current?.();
      detachPreviewIframeEscapeRef.current = null;
      detachPreviewIframeHeightObservers();
      clearPreviewIframeHeightSync();
    };
  }, []);

  function handleIframeLoad(event: React.SyntheticEvent<HTMLIFrameElement>) {
    attachPreviewIframeHeightSync(event.currentTarget);
  }

  return (
    <div
      className="email-builder-preview-frame"
      style={
        viewportMaxWidthPx === undefined ? undefined : { maxWidth: `${viewportMaxWidthPx}px` }
      }
    >
      {showLoadingOverlay ? (
        <EmailBuilderPreviewLoadingFrame
          previewViewport={previewViewport}
          contentWidthPx={contentWidthPx}
          minHeightPx={cachedPreviewIframeMinHeightPx > 0 ? cachedPreviewIframeMinHeightPx : undefined}
        />
      ) : null}
      <iframe
        ref={iframeRef}
        className={
          showLoadingOverlay
            ? "email-builder-preview-iframe email-builder-preview-iframe--measuring"
            : "email-builder-preview-iframe"
        }
        title={iframeTitle}
        srcDoc={prepareEmailBuilderPreviewHtml(previewHtml)}
        sandbox={EMAIL_BUILDER_PREVIEW_IFRAME_SANDBOX}
        scrolling="no"
        style={
          iframeMinHeightPx !== undefined ? { minHeight: `${iframeMinHeightPx}px` } : undefined
        }
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
